import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtIDR, fmtDate, RATE_META } from "@/lib_app/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { GripVertical, Search, Building2 } from "lucide-react";
import Avatar from "@/components/app/Avatar";

const COLUMNS = [
  { rate: 1, ...RATE_META[1] },
  { rate: 2, ...RATE_META[2] },
  { rate: 3, ...RATE_META[3] },
];

const SCOPE_KEY = "kanban_scope";

export default function Kanban() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [scope, setScope] = useState(() => localStorage.getItem(SCOPE_KEY) || "mine");
  const [filterOwner, setFilterOwner] = useState("all");
  const [search, setSearch] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";

  useEffect(() => { localStorage.setItem(SCOPE_KEY, scope); }, [scope]);
  // Owner has no personal prospek — always force team-wide scope
  useEffect(() => { if (isOwner && scope !== "all") setScope("all"); }, [isOwner, scope]);

  const load = async () => {
    try {
      const [p, u, c] = await Promise.all([
        api.get("/prospects"),
        api.get("/users"),
        api.get("/clients"),
      ]);
      setRows(p.data);
      setUsers(u.data);
      setClients(c.data);
    } catch {
      toast.error("Gagal memuat data");
    }
  };
  useEffect(() => { load(); }, []);

  // Map client_id → { logo, name, category }
  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clients]);

  const filtered = useMemo(() => rows.filter((r) => {
    if ((r.status || "active") !== "active") return false;
    if (scope === "mine" && r.owner_id !== me?.user_id) return false;
    if (filterOwner !== "all" && r.owner_id !== filterOwner) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(r.customer.toLowerCase().includes(s) || r.service.toLowerCase().includes(s))) return false;
    }
    return true;
  }), [rows, scope, filterOwner, search, me]);

  const byRate = useMemo(() => {
    const g = { 1: [], 2: [], 3: [] };
    filtered.forEach((r) => g[r.rate]?.push(r));
    return g;
  }, [filtered]);

  const canMove = (row) => me?.role === "owner" || row.owner_id === me?.user_id;

  const onDragStart = (e, prospect) => {
    if (!canMove(prospect)) { e.preventDefault(); return; }
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: prospect.id, fromRate: prospect.rate }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, targetRate) => {
    e.preventDefault();
    setDragOver(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    if (!payload || payload.fromRate === targetRate) return;

    const prospect = rows.find((r) => r.id === payload.id);
    if (!prospect || !canMove(prospect)) {
      toast.error("Hanya pemilik prospek atau owner yang bisa memindahkan");
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === payload.id ? { ...r, rate: targetRate } : r)));
    try {
      await api.patch(`/prospects/${payload.id}/rate`, { rate: targetRate });
      toast.success(`Dipindah ke ${RATE_META[targetRate].label}`);
    } catch (err) {
      setRows((prev) => prev.map((r) => (r.id === payload.id ? { ...r, rate: payload.fromRate } : r)));
      toast.error(err.response?.data?.detail || "Gagal memindahkan");
    }
  };

  return (
    <div className="space-y-6" data-testid="kanban-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Board</p>
          <h1 className="text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>Kanban</h1>
          <p className="text-sm text-zinc-500 mt-1">Geser kartu antar kolom untuk mengubah stage.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
            <Input
              data-testid="kanban-search"
              placeholder="Cari prospek..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-11 w-64 rounded-full bg-white card-soft border-transparent focus-visible:ring-[#090808]/40"
            />
          </div>
          <Select value={filterOwner} onValueChange={setFilterOwner}>
            <SelectTrigger data-testid="kanban-filter-owner" className="w-48 h-11 rounded-full bg-white card-soft border-transparent px-5">
              <SelectValue placeholder="Sales" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Sales</SelectItem>
              {users.filter((u) => u.role !== "owner" && u.role !== "finance").map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  <div className="flex items-center gap-2">
                    <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                    <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Scope toggle — konsisten dengan Prospek. Owner tidak punya prospek pribadi. */}
      {!isOwner && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center rounded-full bg-white card-soft p-1 shrink-0" data-testid="kanban-scope-toggle">
            <button
              onClick={() => setScope("mine")}
              data-testid="kanban-scope-mine"
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                scope === "mine" ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]" : "text-[#626061] hover:text-[#090808]"
              }`}
            >
              Prospek Saya
            </button>
            <button
              onClick={() => setScope("all")}
              data-testid="kanban-scope-all"
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                scope === "all" ? "bg-[#090808] text-white" : "text-[#626061] hover:text-[#090808]"
              }`}
            >
              Semua Tim
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const items = byRate[col.rate] || [];
          const total = items.reduce((s, r) => s + (r.prospect_value || 0), 0);
          const isOver = dragOver === col.rate;
          // Glassy tint per rate: Hot=kemerahan, Warm=orange, Cold=biru
          const glassTint =
            col.rate === 1 ? "linear-gradient(160deg, rgba(255,193,203,0.42) 0%, rgba(255,213,220,0.28) 100%)"
            : col.rate === 2 ? "linear-gradient(160deg, rgba(255,215,170,0.42) 0%, rgba(255,225,190,0.28) 100%)"
            : "linear-gradient(160deg, rgba(190,210,240,0.42) 0%, rgba(210,222,242,0.28) 100%)";
          const glassBorder =
            col.rate === 1 ? "rgba(255,140,155,0.35)"
            : col.rate === 2 ? "rgba(255,180,110,0.35)"
            : "rgba(150,175,220,0.35)";
          return (
            <div
              key={col.rate}
              data-testid={`kanban-col-${col.rate}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(col.rate); }}
              onDragLeave={() => setDragOver((c) => (c === col.rate ? null : c))}
              onDrop={(e) => onDrop(e, col.rate)}
              className={`rounded-2xl flex flex-col min-h-[400px] transition-all ${
                isOver ? "ring-2 ring-[#090808] scale-[1.01]" : ""
              }`}
              style={{
                background: glassTint,
                backdropFilter: "blur(24px) saturate(160%)",
                WebkitBackdropFilter: "blur(24px) saturate(160%)",
                border: `1px solid ${glassBorder}`,
                boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
              }}
            >
              <div className={`px-4 py-3 rounded-t-2xl flex items-center justify-between`} style={{ borderBottom: `1px solid ${glassBorder}` }}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full ${col.pill} text-white font-bold flex items-center justify-center text-xs`}>{col.letter}</div>
                  <span className={`text-sm font-bold text-[#090808]`}>{col.label}</span>
                  <span className="text-xs text-[#626061] tabular-nums">({items.length})</span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#626061] font-num font-semibold tabular-nums">
                  {fmtIDR(total)}
                </span>
              </div>

              <div className="p-3 space-y-2 flex-1">
                {items.length === 0 && (
                  <div className="text-center text-xs text-[#626061] py-12 border-2 border-dashed rounded-xl" style={{ borderColor: glassBorder }}>
                    Tarik kartu ke sini
                  </div>
                )}
                {items.map((r) => {
                  const movable = canMove(r);
                  const client = clientMap[r.client_id];
                  return (
                    <Card
                      key={r.id}
                      draggable={movable}
                      onDragStart={(e) => onDragStart(e, r)}
                      onClick={() => navigate(`/prospects?open=${r.id}`)}
                      data-testid={`kanban-card-${r.id}`}
                      className={`p-4 border-0 rounded-2xl cursor-pointer hover:shadow-[0_10px_24px_rgba(24,27,51,0.14)] transition-shadow group bg-white relative shadow-[0_4px_14px_rgba(24,27,51,0.10)] ${
                        movable ? "" : "opacity-80"
                      }`}
                    >
                      {movable && (
                        <GripVertical className="w-3.5 h-3.5 text-[#A0A3A8] absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                      <div className="flex items-start gap-3">
                        <KanbanClientLogo logo={client?.logo} name={r.customer} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold text-[#090808] truncate">{r.customer}</div>
                          <div className="text-xs text-[#A0A3A8] truncate mt-0.5">{r.service}</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-[#DCE0E7] flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar name={r.owner_name} picture={r.owner_picture} size="xs" />
                          <span className="text-[#626061] font-medium truncate">{r.owner_nickname || r.owner_name}</span>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-num text-xs font-bold text-[#090808] tabular-nums whitespace-nowrap">
                            {fmtIDR(r.prospect_value)}
                          </div>
                          {r.last_follow_up && (
                            <div className="text-[10px] text-[#A0A3A8] tabular-nums mt-0.5">FU {fmtDate(r.last_follow_up)}</div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanClientLogo({ logo, name }) {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  const initials = words.length >= 2
    ? words.slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : (words[0] || "").slice(0, 2).toUpperCase();
  if (logo) {
    return <img src={logo} alt={name} className="w-11 h-11 rounded-2xl object-cover shrink-0 border border-[#DCE0E7]" />;
  }
  return (
    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#DCE0E7] to-[#DCE0E7] text-[#090808] font-bold text-xs flex items-center justify-center shrink-0">
      {initials || <Building2 className="w-4 h-4" />}
    </div>
  );
}
